import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import CtaBand from "@/components/CtaBand";
import { api } from "@/lib/api";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/insights/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="bg-[#F5F8FC]">
        <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="insight-not-found">
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Article not found</h1>
          <p className="mt-3 text-sm text-[#667085]">This article may have been unpublished or removed.</p>
          <Link to="/insights" data-testid="insight-not-found-back" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#A87B1F] hover:text-[#0B1F3A]">
            <ArrowLeft size={15} /> Back to Insights
          </Link>
        </div>
      </section>
    );
  }

  if (!article) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center bg-[#F5F8FC] text-[#A87B1F]">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Seo title={`${article.title} | Incremental Consulting`} description={article.summary} />
      <section className="bg-[#0B1F3A]">
        <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
          <Reveal>
            <Link to="/insights" data-testid="insight-article-back" className="inline-flex items-center gap-2 text-[13px] font-semibold text-white/60 transition-colors hover:text-white">
              <ArrowLeft size={15} /> All Insights
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#9CC3F0]">{article.category}</span>
              <span className="text-[13px] text-white/50">
                {article.published_at ? new Date(article.published_at).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : ""}
              </span>
            </div>
            <h1 className="mt-4 text-balance font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="insight-article-title">
              {article.title}
            </h1>
          </Reveal>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-5 py-14 lg:px-8 lg:py-20">
          {article.image_url ? (
            <img src={article.image_url} alt={article.title} className="w-full rounded-2xl object-cover shadow-[0_20px_50px_-30px_rgba(11,31,58,0.4)]" data-testid="insight-article-image" />
          ) : (
            <div className="flex aspect-[21/9] items-center justify-center rounded-2xl bg-[#F5F8FC] text-[#A87B1F]/30">
              <BookOpen size={40} />
            </div>
          )}
          <div
            className="article-body mt-10"
            data-testid="insight-article-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
          />
        </div>
      </section>
      <CtaBand />
    </>
  );
}
